import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { AdjustmentsHorizontalIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { TextButton } from 'components/buttons';
import { DebouncedSearchInput, SelectField } from 'components/forms';

const DEFAULT_FILTER_CLASS_NAMES = {
  search: 'w-full sm:w-72',
  select: 'w-full sm:w-48',
};

const ACTION_BUTTON_CLASS_NAME = 'inline-flex h-10 items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 text-sm font-medium text-gray-200 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer whitespace-nowrap';

const getDefaultFilterValue = (filter) => {
  if (filter.type === 'search') {
    return '';
  }

  return filter.options?.[0]?.value || '';
};

const renderFilterControl = ({ filter, value, loading, onChange }) => {
  const className = filter.className || DEFAULT_FILTER_CLASS_NAMES[filter.type] || 'w-full';

  if (filter.type === 'search') {
    return (
      <DebouncedSearchInput
        id={`list-filter-${filter.key}`}
        label={filter.label}
        placeholder={filter.placeholder}
        className={className}
        value={value}
        onChange={(event) => onChange({ [filter.key]: event.target.value })}
        loading={loading}
      />
    );
  }

  return (
    <SelectField
      label={filter.label}
      className={className}
      options={filter.options}
      value={value}
      onChange={(nextValue) => onChange({ [filter.key]: nextValue })}
      loading={loading}
    />
  );
};

export default function FilterBar({
  filters,
  values,
  onChange,
  onReset,
  hasActiveFilters,
  loading,
  sort,
}) {
  void sort;

  const [searchResetVersion, setSearchResetVersion] = React.useState(0);

  const primaryFilters = filters.filter((filter) => filter.tier === 'primary');
  const secondaryFilters = filters.filter((filter) => filter.tier === 'secondary');
  const hasSecondaryFilters = secondaryFilters.length > 0;
  const hasActiveSecondaryFilters = secondaryFilters.some((filter) => (
    (values[filter.key] ?? getDefaultFilterValue(filter)) !== getDefaultFilterValue(filter)
  ));
  const getFilterInstanceKey = (filter) => (
    filter.type === 'search' ? `${filter.key}-${searchResetVersion}` : filter.key
  );
  const handleReset = () => {
    setSearchResetVersion((current) => current + 1);
    onReset();
  };

  return (
    <Disclosure>
      {({ open }) => (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            {primaryFilters.map((filter) => (
              <div key={getFilterInstanceKey(filter)}>
                {renderFilterControl({
                  filter,
                  value: values[filter.key] ?? getDefaultFilterValue(filter),
                  loading,
                  onChange,
                })}
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-3 sm:self-end">
              {hasSecondaryFilters && (
                <DisclosureButton className={ACTION_BUTTON_CLASS_NAME}>
                  <div className="relative flex items-center gap-2">
                    <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                    <span>More filters</span>
                    {hasActiveSecondaryFilters && (
                      <>
                        <span className="sr-only">Secondary filters active</span>
                        <span className="absolute -right-2 -top-1 h-2.5 w-2.5 rounded-full bg-indigo-400" aria-hidden="true" />
                      </>
                    )}
                  </div>
                  <ChevronDownIcon
                    className={classNames(
                      'h-4 w-4 transition-transform duration-200 ease-in-out',
                      open && 'rotate-180 transform'
                    )}
                    aria-hidden="true"
                  />
                </DisclosureButton>
              )}

              {hasActiveFilters && (
                <TextButton onClick={handleReset} className="inline-flex items-center gap-2 whitespace-nowrap self-center pb-2">
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  <span>Reset filters</span>
                </TextButton>
              )}
            </div>
          </div>

          {hasSecondaryFilters && (
            <Transition
              enter="transition duration-200 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-100 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <DisclosurePanel className="rounded-lg border border-white/10 bg-gray-900/60 px-4 py-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                  {secondaryFilters.map((filter) => (
                    <div key={getFilterInstanceKey(filter)}>
                      {renderFilterControl({
                        filter,
                        value: values[filter.key] ?? getDefaultFilterValue(filter),
                        loading,
                        onChange,
                      })}
                    </div>
                  ))}
                </div>
              </DisclosurePanel>
            </Transition>
          )}
        </div>
      )}
    </Disclosure>
  );
}